import { type QueryObserverResult } from '@tanstack/react-query'
import { LoaderCircleIcon } from 'lucide-react'
import { createContext, useContext, useMemo } from 'react'
import { Outlet } from 'react-router'

import { Banner } from 'slab'
import { useGetPayrollPaySchedules } from 'utils/web_api'
import { type PayrollPaySchedule } from 'utils/web_api.schemas'

type PayScheduleContextType = {
  paySchedule: PayrollPaySchedule | null;
  paySchedules: PayrollPaySchedule[]
  refetch: ReturnType<typeof useGetPayrollPaySchedules>['refetch'];
}

export const PayScheduleContext = createContext<PayScheduleContextType>({
  paySchedule: null,
  paySchedules: [],
  refetch: async () =>
    ({
      data: null,
      isLoading: false,
      isError: false,
      error: null,
    } as QueryObserverResult<unknown, unknown>),
})

export function usePaySchedule () {
  return useContext(PayScheduleContext)
}

export function PayScheduleProvider () {
  const {
    data: payScheduleData,
    isLoading,
    error,
    refetch,
  } = useGetPayrollPaySchedules()

  const paySchedules: PayrollPaySchedule[] = useMemo(() => {
    if (!payScheduleData?.data) return []

    const sortedPaySchedules = [...payScheduleData.data]
      .filter(schedule => schedule.archived === false && schedule.source === 'manual')
      .sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )

    return sortedPaySchedules
  }, [payScheduleData])

  const currentPaySchedule = paySchedules[0] ?? null

  const value = useMemo(
    () => ({ paySchedule: currentPaySchedule, paySchedules, refetch }),
    [paySchedules, refetch]
  )

  if (isLoading) {
    return (
      <div className='flex grow items-center justify-center'>
        <LoaderCircleIcon size={24} className='animate-spin text-gray-500' />
      </div>
    )
  }

  if (error) {
    return (
      <div className='mt-20 max-w-[45rem] mx-auto'>
        <Banner type='critical' title='Error loading quote'>
          {error instanceof Error && error.message}
        </Banner>
      </div>
    )
  }

  return (
    <PayScheduleContext.Provider value={value}>
      <Outlet />
    </PayScheduleContext.Provider>
  )
}
